import {
  type ComponentType,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { useSnapshot } from 'valtio';

import {
  APPLET_ACCESS_DENIAL_REASON,
  type AppletAccessDenialReason,
  type AppletAccessResult,
  type AppletPermissionContext,
  type Policy,
  evaluatePolicy,
  isAppletAccessAllowedWithUiWarning,
  isAppletAccessDenied,
  isAppletAccessFullAllow,
  isAppletAccessLoading,
  isAppletAccessWarnNoApiKey,
  isAppletAccessWarnSignIn,
} from '@cartwhl/common/appletPolicy';
import {
  getHomepageUrl,
  isNonProduction,
  isServiceApiKeyExpired,
} from '@cartwhl/common/utils';

import {
  appletSignInModalStore,
  appletUpgradeModalStore,
  authStore,
  noticesStore,
  userStore,
} from 'stores/global';

import type { AppletLayoutProps, AppletRouteComponent } from '../applets';
import AppletSignInModal from './AppletSignInModal';
import BatchFeatureUpgradeModal from './BatchFeatureUpgradeModal';
import DefaultAppletAccessDenied from './DefaultAppletAccessDenied';
import DefaultAppletLayout from './DefaultAppletLayout';
import DefaultAppletLoading from './DefaultAppletLoading';

export interface AppletAccessDeniedProps {
  reason: AppletAccessDenialReason;
  message: string;
  loginUrl: string;
}

const APPLET_GUARD_DEBUG = {
  queryParam: 'appletGuardState',
  /** Query values → synthetic `evaluatePolicy` results (non-production only). */
  state: {
    allowed: 'allowed',
    loading: 'loading',
    anonymous: 'anonymous',
    noApiKey: 'noApiKey',
    notAdmin: 'notAdmin',
    warnSignIn: 'warnSignIn',
  },
} as const;

const EXPIRED_API_KEY_NOTICE_MESSAGE =
  'Your account is currently unable to process jobs. Please reach out to support for help.';

let expiredApiKeyNoticeShownForServiceId: string | null = null;

const DENIAL_MESSAGE: Record<AppletAccessDenialReason, string> = {
  [APPLET_ACCESS_DENIAL_REASON.anonymous]:
    'You need to be logged in to use this app.',
  [APPLET_ACCESS_DENIAL_REASON.noApiKey]:
    'Your account is ineligible to use this app. Please upgrade or contact support to get access.',
  [APPLET_ACCESS_DENIAL_REASON.notAdmin]: 'This applet is for admins only.',
};

function getDenialMessage(reason: AppletAccessDenialReason): string {
  return DENIAL_MESSAGE[reason];
}

function getAppletGuardDebugAccessResult(
  search: string,
): AppletAccessResult | undefined {
  if (!isNonProduction()) {
    return undefined;
  }

  const guardState = new URLSearchParams(search).get(
    APPLET_GUARD_DEBUG.queryParam,
  );
  const state = APPLET_GUARD_DEBUG.state;

  switch (guardState) {
    case state.allowed:
      return true;
    case state.loading:
      return 'loading';
    case state.anonymous:
      return {
        allowed: false,
        reason: APPLET_ACCESS_DENIAL_REASON.anonymous,
      };
    case state.noApiKey:
      return {
        allowed: false,
        reason: APPLET_ACCESS_DENIAL_REASON.noApiKey,
      };
    case state.notAdmin:
      return {
        allowed: false,
        reason: APPLET_ACCESS_DENIAL_REASON.notAdmin,
      };
    case state.warnSignIn:
      return { allowed: true, warnSignIn: true };
    default:
      return undefined;
  }
}

/** Applet shape needed for the guard (policy, path, component). */
export type AppletGuardConfig = {
  id?: string;
  label: string;
  description?: string;
  policy: Policy;
  path: string;
  component: AppletRouteComponent;
  layout?: ComponentType<AppletLayoutProps>;
  loading?: ComponentType;
  accessDenied?: ComponentType<AppletAccessDeniedProps>;
};

export interface AppletGuardProps {
  applet: AppletGuardConfig;
}

export default function AppletGuard({ applet }: AppletGuardProps) {
  const { policy, component: Component } = applet;
  const appletGuardKey = applet.id ?? applet.path;
  const prevAppletGuardKeyRef = useRef<string | null>(null);
  const handledSignInRequestRef = useRef(0);
  const handledUpgradeRequestRef = useRef(0);
  const { pathname, search } = useLocation();
  const Layout = applet.layout ?? DefaultAppletLayout;
  const LoadingComponent = applet.loading ?? DefaultAppletLoading;
  const AccessDeniedComponent =
    applet.accessDenied ?? DefaultAppletAccessDenied;
  const DeniedLayout = applet.accessDenied ? Layout : DefaultAppletLayout;
  const componentFallback = <LoadingComponent />;
  const authState = useSnapshot(authStore.state);
  const {
    apiKey,
    isCartwheeler,
    loading: userStoreLoading,
    organizationServiceAccounts,
    userInfo,
  } = useSnapshot(userStore.state);
  const signInModalRequest = useSnapshot(appletSignInModalStore);
  const upgradeModalRequest = useSnapshot(appletUpgradeModalStore);

  const loginUrl = useMemo(() => {
    const redirectTo = `apps${pathname}${search}`;
    return getHomepageUrl(
      `/login?redirectTo=${encodeURIComponent(redirectTo)}`,
    );
  }, [pathname, search]);

  const signUpUrl = useMemo(() => {
    const redirectTo = `apps${pathname}${search}`;
    return getHomepageUrl(
      `/sign-up?redirectTo=${encodeURIComponent(redirectTo)}`,
    );
  }, [pathname, search]);

  const [upgradeModalDismissed, setUpgradeModalDismissed] = useState(false);

  const [signInModalDismissed, setSignInModalDismissed] = useState(false);

  const ctx: AppletPermissionContext = {
    authStatus: authState.authStatus,
    apiKey,
    isCartwheeler,
    userStoreLoading,
    userOrganizationId: userInfo.organizationID ?? '',
  };

  // Dev-only query param for previewing guard states without changing auth.
  const access =
    getAppletGuardDebugAccessResult(search) ?? evaluatePolicy(policy, ctx);

  const isSignInPreview = isAppletAccessWarnSignIn(access);

  const isWarnNoApiKeyPreview = isAppletAccessWarnNoApiKey(access);

  useLayoutEffect(() => {
    if (prevAppletGuardKeyRef.current === appletGuardKey) {
      return;
    }
    if (prevAppletGuardKeyRef.current !== null) {
      appletSignInModalStore.openRequestVersion = 0;
      handledSignInRequestRef.current = 0;
      appletUpgradeModalStore.openRequestVersion = 0;
      handledUpgradeRequestRef.current = 0;
    }
    prevAppletGuardKeyRef.current = appletGuardKey;
  }, [appletGuardKey]);

  useEffect(() => {
    if (isAppletAccessLoading(access) || isAppletAccessDenied(access)) {
      return;
    }

    const serviceID = apiKey.data?.serviceID;
    if (apiKey.status !== 'ready' || !serviceID) {
      return;
    }

    if (
      !isServiceApiKeyExpired(serviceID, organizationServiceAccounts) ||
      expiredApiKeyNoticeShownForServiceId === serviceID
    ) {
      return;
    }

    expiredApiKeyNoticeShownForServiceId = serviceID;
    noticesStore.error(EXPIRED_API_KEY_NOTICE_MESSAGE);
  }, [access, apiKey, organizationServiceAccounts]);

  useEffect(() => {
    const v = signInModalRequest.openRequestVersion;
    if (v === 0 || !isSignInPreview) {
      return;
    }
    if (v <= handledSignInRequestRef.current) {
      return;
    }
    handledSignInRequestRef.current = v;
    setSignInModalDismissed(false);
  }, [isSignInPreview, signInModalRequest.openRequestVersion]);

  useEffect(() => {
    const v = upgradeModalRequest.openRequestVersion;
    if (v === 0 || !isWarnNoApiKeyPreview) {
      return;
    }
    if (v <= handledUpgradeRequestRef.current) {
      return;
    }
    handledUpgradeRequestRef.current = v;
    setUpgradeModalDismissed(false);
  }, [isWarnNoApiKeyPreview, upgradeModalRequest.openRequestVersion]);

  const dismissUpgradeModal = useCallback(() => {
    setUpgradeModalDismissed(true);
  }, []);

  const dismissSignInModal = useCallback(() => {
    setSignInModalDismissed(true);
  }, []);

  const showUpgradeModal =
    isAppletAccessWarnNoApiKey(access) && !upgradeModalDismissed;

  const showSignInModal =
    isAppletAccessWarnSignIn(access) && !signInModalDismissed;

  if (isAppletAccessFullAllow(access)) {
    return (
      <Layout title={applet.label} subtitle={applet.description}>
        <Suspense fallback={componentFallback}>
          <Component />
        </Suspense>
      </Layout>
    );
  }

  if (isAppletAccessAllowedWithUiWarning(access)) {
    return (
      <Layout title={applet.label} subtitle={applet.description}>
        {isAppletAccessWarnNoApiKey(access) ? (
          <BatchFeatureUpgradeModal
            open={showUpgradeModal}
            onDismiss={dismissUpgradeModal}
          />
        ) : null}
        {isAppletAccessWarnSignIn(access) ? (
          <AppletSignInModal
            open={showSignInModal}
            onDismiss={dismissSignInModal}
            loginUrl={loginUrl}
            signUpUrl={signUpUrl}
          />
        ) : null}
        <Suspense fallback={componentFallback}>
          <Component />
        </Suspense>
      </Layout>
    );
  }

  if (isAppletAccessLoading(access)) {
    return (
      <Layout title={applet.label}>
        <LoadingComponent />
      </Layout>
    );
  }

  const { reason } = access;
  const message = getDenialMessage(reason);

  return (
    <DeniedLayout title={applet.label} subtitle={applet.description}>
      <AccessDeniedComponent
        reason={reason}
        message={message}
        loginUrl={loginUrl}
      />
    </DeniedLayout>
  );
}
